import { useCallback, useMemo } from 'react';
import { atom, useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

interface ChatInputDraft {
    message: string;
    attachments: File[];
}

const chatInputDraftMessageAtom = atomWithStorage<Record<string, string>>(
    'chat-input-draft',
    {},
    createJSONStorage(() => sessionStorage),
);

const chatInputDraftAttachmentsAtom = atom<Record<string, File[]>>({});

/**
 * Session-persisted drafts for chat input. Message text is persisted to
 * `sessionStorage` while file attachments are kept in memory for the active session.
 * @param conversationID The ID of the conversation. If `0` or less, drafts are disabled.
 * @param professionalID The company inbox professional ID if in a company inbox context.
 */
export function useChatInputDraft(
    conversationID: number,
    professionalID: number | null,
) {
    const [draftMessages, setDraftMessages] = useAtom(
        chatInputDraftMessageAtom,
    );
    const [draftAttachments, setDraftAttachments] = useAtom(
        chatInputDraftAttachmentsAtom,
    );
    const draftKey = useMemo(() => {
        return getChatInputDraftKey(conversationID, professionalID);
    }, [conversationID, professionalID]);

    const draft = useMemo<ChatInputDraft>(() => {
        if (!draftKey) {
            return { message: '', attachments: [] };
        }
        return {
            message: draftMessages[draftKey] ?? '',
            attachments: draftAttachments[draftKey] ?? [],
        };
    }, [draftKey, draftMessages, draftAttachments]);

    const clearDraft = useCallback(() => {
        if (!draftKey) {
            return;
        }
        setDraftMessages((current) => {
            if (!(draftKey in current)) {
                return current;
            }
            const next = { ...current };
            delete next[draftKey];
            return next;
        });
        setDraftAttachments((current) => {
            if (!(draftKey in current)) {
                return current;
            }
            const next = { ...current };
            delete next[draftKey];
            return next;
        });
    }, [draftKey, setDraftMessages, setDraftAttachments]);

    const saveDraft = useCallback(
        ({ message, attachments }: ChatInputDraft) => {
            if (!draftKey) {
                return;
            }
            const hasContent = !!message.trim() || attachments.length > 0;
            if (!hasContent) {
                clearDraft();
                return;
            }
            setDraftMessages((current) => {
                return {
                    ...current,
                    [draftKey]: message.trim(),
                };
            });
            setDraftAttachments((current) => {
                return {
                    ...current,
                    [draftKey]: attachments,
                };
            });
        },
        [draftKey, clearDraft, setDraftMessages, setDraftAttachments],
    );

    return {
        draft,
        saveDraft,
        clearDraft,
    };
}

function getChatInputDraftKey(
    conversationID: number,
    professionalID: number | null,
) {
    if (conversationID <= 0) {
        return null;
    }
    if (professionalID && professionalID > 0) {
        return `company:${conversationID}:${professionalID}`;
    }
    return `personal:${conversationID}`;
}
